import { useMemo, useRef } from 'react';
import { dragRace, laneAt, raceableCar } from '../../domain/race/dragRace';
import { raceMoments } from '../../domain/race/raceMoments';
import useRaceClock from '../motion/useRaceClock';

/**
 * A race, bound to the two cars on screen (SPEC-008 PR-8.5).
 *
 * Everything about WHAT happens is `domain/race`; this hook is the three things
 * that cannot be pure — which calibrations are worth keeping, when the frame
 * loop runs, and whether this reader wants motion at all. The moments the strip
 * stops at are detected in the domain and handed to the clock as marks, so the
 * question of WHICH instants matter never becomes a question about frames.
 *
 * THE MEMOISATION IS THE POINT, and it is split at the seam the cost sits on.
 * Calibrating a car is roughly forty integrations and it depends only on the
 * record; running a race is one and it depends on the conditions too. The reader
 * changes the surface far more often than they change the car, so calibrations
 * are cached against the record for the life of the module and only the race
 * itself is recomputed when a chip is tapped.
 */

/**
 * Calibrated cars, kept for the life of the module.
 *
 * At module scope rather than in a ref, for the reason `useCarCatalogue` caches
 * its promise there: two components mounting in one tick, or a reader going to
 * the garage and coming back, should not pay for the same forty integrations
 * twice. Keyed by the RECORD rather than by its id, so a catalogue that
 * genuinely reloaded gets a fresh calibration rather than one computed from
 * figures that have since changed.
 */
const calibrated = new WeakMap();

function dynamicsFor(car) {
    if (!car) {
        return null;
    }

    if (!calibrated.has(car)) {
        calibrated.set(car, raceableCar(car));
    }

    return calibrated.get(car);
}

/**
 * Does this reader want motion?
 *
 * Read once at call time through a defaulted parameter rather than subscribed
 * to. A reader who changes the system setting mid-race is a reader who will get
 * the new answer on the next race, and a `matchMedia` listener for that is a
 * subscription this screen would carry for the life of the session to serve a
 * case that does not happen.
 */
function prefersReducedMotion(matchMedia) {
    const query = matchMedia || (typeof window === 'undefined' ? null : window.matchMedia);

    return Boolean(query?.call(window, '(prefers-reduced-motion: reduce)')?.matches);
}

/**
 * The race, and where both cars are right now.
 *
 * `positions` is what the strip draws and it is derived rather than stored: the
 * clock owns one number and everything on screen is a function of it, so there
 * is nothing for a lane and its clock to disagree about. It is the same argument
 * the duel's timer makes for decrementing the run's own seconds rather than
 * reading a clock in two places.
 */
export default function useDragRace({ cars, conditions, matchMedia = null }) {
    const reducedMotion = useRef(prefersReducedMotion(matchMedia)).current;
    const race = useMemo(() => {
        const entries = cars.map((car) => ({ carId: car?.id ?? null, dynamics: dynamicsFor(car) }));

        return dragRace(entries, conditions);
    }, [cars, conditions]);

    const moments = useMemo(() => (race ? raceMoments(race) : []), [race]);
    // Memoised separately from the moments themselves: the clock takes the marks
    // as a dependency of its own frame loop, and a fresh array on every render
    // would restart the race on every tick of it.
    const holds = useMemo(() => moments.map((moment) => moment.seconds), [moments]);
    const clock = useRaceClock(race?.duration ?? 0, { holds, reducedMotion });

    const positions = useMemo(() => (race?.lanes ?? []).map((lane) => {
        // Before the reader has asked for anything both cars are on the line,
        // rather than wherever the first sample happens to sit.
        const at = clock.status === 'idle'
            ? { metres: 0, kmh: 0, gear: 1, wheelspin: false }
            : laneAt(lane, clock.seconds);

        return {
            carId: lane.carId,
            metres: at.metres,
            share: Math.min(1, at.metres / race.distance),
            kmh: at.kmh,
            gear: at.gear,
            // Only while frames are arriving: a strip frozen on the result
            // should not be showing a car mid-spin for ever.
            wheelspin: clock.status === 'running' && at.wheelspin,
            arrived: lane.finished && clock.seconds >= lane.seconds,
        };
    }), [race, clock.seconds, clock.status]);

    /**
     * How far apart the two cars are, right now, in both of the units that
     * matter.
     *
     * They are TWO INDEPENDENT FACTS and the interesting case is exactly the one
     * where they disagree: a car twelve metres behind and fourteen km/h faster is
     * a car catching, and a readout that named one leader would have to pick
     * which half of that to tell. So each carries its own leader.
     *
     * Derived rather than stored, like `positions` and for the same reason: the
     * clock owns one number and everything on screen is a function of it, so
     * there is nothing here for a gap and a lane to disagree about.
     */
    const gap = useMemo(() => {
        if (positions.length !== 2 || !race) {
            return null;
        }

        const [first, second] = positions;
        const leaderOf = (value) => {
            if (Math.abs(value) < 0.05) {
                return null;
            }

            return value > 0 ? first.carId : second.carId;
        };

        // AT THE LINE the live reading stops being the true one. Both cars are
        // clamped at 400 metres once they have finished, so a position gap
        // collapses to nothing — which would report the Esprit as a metre behind
        // an F40 that beat it by two and a half seconds. The honest gap at the
        // end is the one the race itself judged: where the loser was when the
        // winner crossed.
        if (clock.status === 'finished') {
            const trap = race.lanes.map((lane) => lane.trapKmh);

            return {
                metres: race.marginMetres,
                metresLeader: race.winner,
                kmh: Math.abs(trap[0] - trap[1]),
                kmhLeader: leaderOf(trap[0] - trap[1]),
            };
        }

        return {
            metres: Math.abs(first.metres - second.metres),
            metresLeader: leaderOf(first.metres - second.metres),
            kmh: Math.abs(first.kmh - second.kmh),
            kmhLeader: leaderOf(first.kmh - second.kmh),
        };
    }, [positions, race, clock.status]);

    /**
     * The moments the race has already been through, each pinned to where it
     * happened on its car's curve.
     *
     * The freeze explains a moment once, in words, and then the strip moves on
     * and the words go. These are what is LEFT of it: a mark on the chart at the
     * point it happened, carrying its one figure, so a reader arriving at the
     * finish can still see where the wheelspin was and where the lead changed.
     * It is the job the static band's measured points used to do on this screen,
     * done for the run the reader actually watched.
     *
     * Derived from the clock like everything else here, so a mark cannot appear
     * before the moment it describes.
     */
    const markers = useMemo(() => {
        if (!race || clock.status === 'idle') {
            return [];
        }

        return moments
            .filter((moment) => moment.seconds <= clock.seconds)
            .map((moment) => {
                const laneIndex = race.lanes.findIndex((lane) => lane.carId === moment.carId);
                const at = laneAt(race.lanes[Math.max(0, laneIndex)], moment.seconds);

                return {
                    key: moment.key,
                    carId: moment.carId,
                    laneIndex: Math.max(0, laneIndex),
                    values: moment.values,
                    metres: at.metres,
                    kmh: at.kmh,
                };
            });
    }, [race, moments, clock.seconds, clock.status]);

    return {
        race,
        positions,
        gap,
        markers,
        moments,
        // The moment the strip is stopped at, or null while it is moving. The
        // screen draws one region for it either way, so the region's height is
        // reserved and a hold never shifts the page under the reader.
        moment: clock.holdIndex === null ? null : moments[clock.holdIndex],
        seconds: clock.seconds,
        status: clock.status,
        rate: clock.rate,
        reducedMotion,
        run: clock.start,
        skip: clock.skip,
    };
}
